<template>
  <div class="d-flex flex-column page-wrapper welcome">
    <page-header v-if="!uploading">Apply for a Loan at <strong> {{ creditUnionName }}</strong></page-header>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!isIpAllowed">
      <span v-html="ipRestrictedMessage"></span>
    </div>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!uploading && !uploadFailed && uploadSuccess && isIpAllowed">
      <div class="d-flex flex-column explanation-text">
        <p>
          We are so glad you've decided to take out a loan with us. Just a few things you'll need before we get 
          started. <strong>Please note that unless otherwise indicated, most fields in this form are required.</strong>
        </p>
        <div class="d-flex flex-column align-self-center checkmark-list">
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">A valid government issued ID</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">A valid email address</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Social Security Number</p>
          </div>
          <div v-if="referenceRequired" class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text" v-if="referenceCount === 1">{{ referenceCount }} personal reference</p>
            <p class="checkmark-text" v-if="referenceCount > 1">{{ referenceCount }} personal references</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Qualify for <a href="https://www.ascentra.org/belong/join/become-a-member" target="_blank">membership with {{ creditUnionName }}</a></p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Read and accept the agreement</p>
          </div>
        </div>
      </div>
      <div class="d-flex flex-column justify-content-start align-items-start form-container">
        <b-form @submit.prevent="readAgreement" novalidate>
          <b-form-group>
            <label class="form-label">What <a href="https://www.ascentra.org/borrow/vehicle-loans" target="_blank">type of loan</a> are you shopping for?</label>
            <b-button-group vertical class="btn-width" :id="$id('loancategories')">
              <b-button  class="btn" variant="outline-primary" :pressed="opt.coreId === selectedLoanCategory.coreId" @click="selectLoanCategory(opt)" v-for="opt in loanCategories" :key="opt.coreId">{{ opt.name }}</b-button>
            </b-button-group>
          </b-form-group>
          <b-form-group v-if="selectedLoanCategory.coreId && loanTypes.length > 1">
            <label class="form-label"><span v-if="selectedLoanCategory.name === 'Vehicle Loan'">Is this a new or used vehicle?</span><span v-else></span></label>
            <b-button-group vertical class="btn-width" :id="$id('loantypes')">
              <b-button class="btn" variant="outline-primary" :pressed="opt.coreId === selectedLoanType.coreId" @click="selectLoanType(opt)" v-for="opt in loanTypes" :key="opt.coreId">{{ opt.name }}</b-button>
            </b-button-group>
          </b-form-group>
          <div v-if="selectedLoanType.coreId">          
            <div ref="basicLoanQuestions">
              <b-form-group v-if="eligiblePurposes.length > 1">
                <label class="form-label">What is the purpose for this loan?</label>
                <b-form-select class="form-control" :id="$id('loanpurpose')" v-model="selectedLoanPurposeId" :options="eligiblePurposes" value-field="coreId" text-field="name">
                  <template #first>
                    <b-form-select-option :value="''" disabled>Select a Purpose</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
              <div v-if="selectedLoanPurposeId">
                  <auto-loan-questions ref="autoLoanQuestionsRef" v-if="selectedLoanType.collateralCategory === 0" :collateral-types="selectedLoanPurpose.eligibleCollateralTypes"></auto-loan-questions>
                  <b-form-group v-if="eligibleTerms.length > 0" class="form-label" label="How long would you like the loan to be for?">
                      <b-button-group vertical class="btn-width" :id="$id('eligibleTerms')">                        
                          <b-button variant="outline-primary" :pressed="opt.coreId === selectedLoanTerm.coreId" @click="selectLoanTerm(opt)" v-for="opt in eligibleTerms" :key="opt.coreId">
                            {{ opt.name }} ({{ opt.minAmount | currency }} - {{ opt.maxAmount | currency }})
                          </b-button>                                                                    
                      </b-button-group>                      
                      <div class="field-error-message" v-if="!$v.selectedLoanTerm.required && $v.selectedLoanTerm.$dirty">Please select how long you would like the loan to be for</div>        
                  </b-form-group> 
                  <b-form-group class="form-label" label="How much money are you requesting?">
                      <b-input-group>
                        <b-input-group-prepend is-text><b>$</b></b-input-group-prepend>
                        <currency-input class="form-control" :id="$id('loanamount')" inputmode="decimal" v-model="requestedLoanAmount" :currency="null"/>
                      </b-input-group>
                      <div class="field-error-message" v-if="!$v.requestedLoanAmount.required && $v.requestedLoanAmount.$dirty">Please enter the amount of money you need</div>
                      <div class="field-error-message" v-if="!$v.requestedLoanAmount.decimal && $v.requestedLoanAmount.$dirty">Please enter a valid amount</div>
                      <div class="field-error-message" v-if="!$v.requestedLoanAmount.between && $v.requestedLoanAmount.$dirty">The requested loan amount must be between {{ selectedLoanTerm.minAmount | currency }} - {{ selectedLoanTerm.maxAmount | currency }}</div>
                  </b-form-group>                  
              </div>
              <div v-else>
                <b-form-group class="form-label" label="How much money are you requesting?">
                    <b-input-group>
                      <b-input-group-prepend is-text><b>$</b></b-input-group-prepend>
                      <currency-input class="form-control" :id="$id('loanamount')" inputmode="decimal" v-model="requestedLoanAmount" :currency="null"/>
                    </b-input-group>
                    <div class="field-error-message" v-if="!$v.requestedLoanAmount.required && $v.requestedLoanAmount.$dirty">Please enter the amount of money you need</div>
                    <div class="field-error-message" v-if="!$v.requestedLoanAmount.decimal && $v.requestedLoanAmount.$dirty">Please enter a valid amount</div>
                    <div class="field-error-message" v-if="!$v.requestedLoanAmount.between && $v.requestedLoanAmount.$dirty">The requested loan amount must be between {{ selectedLoanTerm.minAmount | currency }} - {{ selectedLoanTerm.maxAmount | currency }}</div>
                </b-form-group>
              </div>
              <b-form-group class="form-label" label="Select the application type">
                  <b-button-group vertical class="btn-width" :id="$id('apptype')">                        
                      <b-button variant="outline-primary" :pressed="option.value === jointBorrower" @click="selectJointBorrower(option)" v-for="option in jointBorrowerOptions" :key="option.value">{{ option.name }}</b-button>
                  </b-button-group>
                  <div class="field-error-message" v-if="!$v.jointBorrower.required && $v.jointBorrower.$dirty">Please select if you'd like to add a coborrower</div>
              </b-form-group>
              <p v-if="jointBorrower === true">
                  <strong>This is important!</strong><br> The Joint Applicant must be present for the application process. 
                  In addition, the Joint Applicant agrees to all disclosures included in the application process. They will also have access to make transactions on your account.
              </p> 
            </div>
          </div>
          <div class="d-flex flex-column justify-content-between align-items-center final-action-div">
            <b-button variant="secondary" type="submit" class="btn btn-width" :id="$id('next')" name="readAgreementBtn" :disabled="nextDisabled">Read &amp; Accept Agreement</b-button>
          </div>
        </b-form>
      </div>
    </div>
    <div class="container d-flex flex-column align-items-center page-container justify-content-center flex-grow-1" v-if="uploading">
      <p class="processing-text">Please wait while we get your application ready</p>
      <img src="@/assets/img/light/activity.gif" class="processing-img" />
    </div>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!uploading && uploadFailed">
      <p class="processing-text">
        There was an error loading your application.<br /> Please
        <a href="#" target="_blank">contact us</a> at {{ creditUnionPhone }}
      </p>
    </div>
    <page-footer :class="{ 'hidden': uploading }" />
    <b-modal ref="disclosureModalRef" id="disclosureModal" size="lg" scrollable body-text-variant="dark" header-text-variant="dark" centered title="Agreement">
      <span id="disclosure" v-html="agreementText"></span>
      <div class="d-flex flex-column justify-content-between align-items-center margin-top-40 margin-bottom-15">
          <b-button variant="primary" @click="email()" class="btn width-240">
              <span><font-awesome-icon icon="envelope"/></span> {{ $t('message.emailAgreement') }}
          </b-button>        
          <div class="height-10"></div>  
          <b-button variant="primary" @click="print()" class="btn width-240">
              <span><font-awesome-icon icon="print"/></span> {{ $t('message.printAgreement') }}
          </b-button>                      
      </div>
      <div slot="modal-footer" class="modal-footer">
          <b-button variant="outline-secondary" class="modal-btn" @click="cancel">{{ $t('message.cancel') }}</b-button>
          <b-button variant="secondary" class="modal-btn" @click="acceptAgreement">Accept Agreement</b-button>
      </div>
    </b-modal>
    <b-modal ref="emailModal" body-text-variant="dark" header-text-variant="dark" centered>
        Please enter the email address you wish to send this agreement to            
        <b-form @submit.prevent="sendEmail" novalidate class="form-container">
          <b-form-group class="form-label" label="Email Address">
            <b-form-input type="email" :id="$id('emailAddress')" v-model="emailAddress" />           
            <div class="field-error-message" v-if="!$v.emailAddress.required && $v.emailAddress.$dirty">You must provide a valid Email Address</div>        
            <div class="field-error-message" v-if="!$v.emailAddress.email && $v.emailAddress.$dirty">Please enter a valid Email Address</div>        
          </b-form-group>
        </b-form>
        <div slot="modal-footer">
            <b-button variant="secondary" @click="sendEmail()">Send Email</b-button>
        </div>
    </b-modal>
    <b-modal ref="invalidEmailModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Error Sending Email">
        We were unable to send the email, please confirm your email address and retry
    </b-modal>
    <b-modal ref="emailSentModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Email Sent">
        The agreement has been sent to {{ emailAddress }}
    </b-modal>
    <b-modal ref="errorModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Missing Information">{{ $t('message.missingRequiredFieldsMsg') }}</b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import VueGtm from '@gtm-support/vue2-gtm';
import { email, required, decimal, between, requiredIf } from "vuelidate/lib/validators";
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import AutoLoanQuestions from "@/views/AutoLoanQuestions.vue";

export default {
  name: "Welcome",
  components: {
    AutoLoanQuestions,
  },
  created() {    
    this.uploading = true;
    let self = this;
    this.getAppConfig().then((config) => {
      if(config) {
        // This is for vue-head to update the header once we have the config
        self.$emit('updateHead');

        this.getPortalContent().then(async (success) => {
          if(success) {     
            let headerContents = this.getPageContent('Header');
            let gtmId = headerContents.find(content => content.contentName === 'GTMID');
            
            if(gtmId) {
              Vue.use(VueGtm, {
                id: gtmId.content,
                enabled:true,                
                vueRouter: this.$router,
                debug: true,
              });              
            } 
            
            this.pageContent = this.getPageContent('Welcome');
            let isValidIp = true;
            if(this.ipCheckRequired) { 
              isValidIp = await this.ipallowedCheck();
            }

            // CE-255 Let's use vue-head to add external scripts from the config DB
            let configuredScripts = this.getPageContent('ExternalScripts');
            if(configuredScripts) {
              for(let i = 0; i < configuredScripts.length; i++) {
                let script = configuredScripts[i];
                console.info('script found', script);
                let record = {
                  type: 'text/javascript',
                  src: script.content,
                  async: true,
                  body: script.languageCode === 'BODY'
                };

                self.scripts.push(record);
              }
            }

            // This is for vue-head to update the header once we have the config
            self.$emit('updateHead');

            if(isValidIp) {
              this.initApp(config);              
              this.uploadFailed = false;
              this.uploadSuccess = true;
            } else {
              this.isIpAllowed = false;              
              this.$gtm.trackEvent({
                event: null, // Event type [default = 'interaction'] (Optional)
                category: 'Security',
                action: 'submit',
                label: 'IP Allowed Check Failed',
                value: 5000,
                noninteraction: true, // Optional
            });
            }
          } else {
            this.uploadFailed = true;
            this.uploadSuccess = false;
          }
          this.uploading = false;
        });
      } else {
        this.uploadFailed = true;
        this.uploadSuccess = false;
        this.uploading = false;
      }
    });
  },
  data: function () {
    return {
      pageContent: [],
      uploading: false,
      uploadFailed: false,
      uploadSuccess: false,
      loanCategories: [],
      agreementAccepted: false,
      jointBorrowerOptions: [
        { id: '1', name: 'Individual Loan', value: false },
        { id: '2', name: 'Joint Loan', value: true }
      ],
      isIpAllowed: true,      
      scripts: [],
    };
  },
  head: {
    title: function() {
      return { 
        inner: 'Apply for a Loan',
        complement: this.creditUnionName
      }
    },
    meta: [
      { name: 'application-name', content: 'Consumer Gravitation - New Loan'}
    ],
    script: function() {
        return this.scripts;
    }
  },
  computed: {
    ...mapState([
      "creditUnion",
      "eligibilityRequirementsUrl",
      "availableLoanTypesUrl",
      "disclosureAsHtml",
      "loanCategoryId",
      "loanTypeId",
      'loanPurposeId',
      'loanTermId',
      "loanTerm",
      "loanAmount",
      "isJointApplication",
      "ipCheckRequired",
      "primaryReferenceCount",
      "jointReferenceCount",
      'challengeQuestionsEnabled'
    ]),
    ...mapGetters([
      'getPageContent',
      'primaryReferenceRequired',
      'jointReferenceRequired',
    ]),
    creditUnionName: {
      get() {
        return this.creditUnion.name;
      }
    },
    creditUnionPhone: {
      get() {
        return this.creditUnion.phoneNumber;
      }
    },
    agreementText: {
      get() {
        var text = this.pageContent.find(content => content.contentName === 'Disclosure');
        return text ? text.content : '';
      }
    },
    ipRestrictedMessage: {
      get() {        
        var text = this.pageContent.find(content => content.contentName === 'IP Restricted Message');
        return text ? text.content : '';
      }
    },
    referenceRequired: {
      get() {
        return this.primaryReferenceRequired || this.jointReferenceRequired;
      }
    },
    referenceCount: {
      get() { // todo this seems weird. Maybe the api should return just one referenceCount instead of primary and joint.
        if(this.primaryReferenceCount) {
          return this.primaryReferenceCount;
        } else if (this.jointReferenceCount) {
          return this.jointReferenceCount;
        }
      }
    },
    loanTypes: {
      get() {
        var loanTypes = this.selectedLoanCategory.eligibleLoanTypes;
        if(loanTypes) {
          return loanTypes;
        }
        return [];
      }
    },
    eligiblePurposes: {
      get() {
        var eligiblePurposes = this.selectedLoanType.eligiblePurposes;
        if(eligiblePurposes) {
          return eligiblePurposes;
        }
        return [];
      }
    },
    eligibleTerms: {
      get() {
        var eligibleTerms = this.selectedLoanPurpose.eligibleTerms;
        if(eligibleTerms) {
          return eligibleTerms;
        }
        return [];
      }
    },
    selectedLoanCategory: {
      get() {
        var category = this.loanCategories.find(item => item.coreId === this.loanCategoryId);
        if(category) {
          return category;
        }
        return {};
      },
      set(value) {
        this.updateLoanCategory({ loanCategoryId: value.coreId });
      }
    },
    selectedLoanType: {
      get() {
        var loanType = this.loanTypes.find(item => item.coreId === this.loanTypeId);
        if(loanType) {
          return loanType;
        }
        return {};
      },
      set(value) {
        this.updateLoanType({ loanTypeId: value.coreId, loanType: value.name, loanDefaultId: value.loanDefaultId });
      }
    },
    selectedLoanPurpose: {
      get() {
        var loanPurpose = this.eligiblePurposes.find(item => item.coreId === this.loanPurposeId);
        if(loanPurpose) {
          return loanPurpose;
        }
        return {};
      },
      set(value) {
        this.updateLoanPurpose({ loanPurposeId: value.coreId, loanPurpose: value.name, purposeId: value.purposeId });
      }
    },
    selectedLoanPurposeId: {
      get() {
        return this.selectedLoanPurpose.coreId ? this.selectedLoanPurpose.coreId : '';
      },
      set(value) {
        var loanPurpose = this.eligiblePurposes.find(item => item.coreId === value);
        if(loanPurpose) {
          this.selectLoanPurpose(loanPurpose);
        } else {
          this.selectLoanPurpose({});
        }
      }
    },
    selectedLoanTerm: {
      get() {
        var term = this.eligibleTerms.find(item => item.coreId === this.loanTermId);
        if(term) {
          return term;
        }
        return {};
      },
      set(value) {
        this.updateLoanTerm({ term: value });
      }
    },
    requestedLoanAmount: {
      get() {
        return this.loanAmount;
      },
      set(value) {
        this.updateLoanAmount({ loanAmount: value });
      }
    },
    jointBorrower: {
      get() {
        return this.isJointApplication;
      },
      set(value) {
        this.updateIsJointApplication(value);
      }
    },
    nextDisabled: {
      get() {
        if(this.eligibleTerms.length > 0) {
          return this.selectedLoanTerm.coreId == null || this.jointBorrower === null ||  !( this.requestedLoanAmount > 0);
        } else {
          return !(this.requestedLoanAmount > 0);
        }
      },
    },
    emailAddress: {
      get() {
        return this.$store.state.primaryEmail.emailAddress;
      },
      set(value) {
        this.updateApplicantEmail({ isPrimary: true, emailAddress: value });
      }
    },
  },
  methods: {
    ...mapActions([
      'getAppConfig',
      'getPortalContent',
      'ipallowedCheck',
      'sendEsignAgreement'
    ]),
    ...mapMutations([
      'updateLoanCategory',
      'updateLoanType',
      'updateLoanPurpose',
      'updateLoanTerm',
      'updateLoanAmount',
      'updateIsJointApplication',
      'updateApplicantEmail'
    ]),
    initApp: function(config) {
      this.loanCategories = config.eligibleLoanCategories;
    },
    selectLoanCategory: function (opt) {   
      console.info('selected loan category: ', opt);
      this.selectedLoanType = {};
      this.selectedLoanPurpose = {};
      this.selectedLoanTerm = {};
      this.requestedLoanAmount = null;
      this.jointBorrower = null;
      this.selectedLoanCategory = opt;
      if(this.loanTypes.length == 1) {
        this.selectLoanType(this.loanTypes[0]);
      }
    },
    selectLoanType: function (opt) {   
      console.info('selected loan type: ', opt);
      this.selectedLoanPurpose = {};
      this.selectedLoanTerm = {};
      this.requestedLoanAmount = null;
      this.jointBorrower = null;
      this.selectedLoanType = opt;
      if(this.eligiblePurposes.length == 1) {
        this.selectLoanPurpose(this.eligiblePurposes[0]);
      }
      this.$nextTick(function () {
        if(this.$refs.autoLoanQuestionsRef) {
          this.$refs.autoLoanQuestionsRef.reset();
        }
      });
    },
    selectLoanPurpose: function(opt) {
      console.info('selected loan purpose: ', opt);
      this.selectedLoanTerm = {};
      this.selectedLoanPurpose = opt;
      if(this.eligibleTerms.length == 1) {
        this.selectLoanTerm(this.eligibleTerms[0]);
      }
    },
    selectLoanTerm: function(opt) {
      console.info('selected loan term: ', opt);
      this.selectedLoanTerm = opt;
    },
    selectJointBorrower: function(opt) {
      this.jointBorrower = opt.value;
    },
    readAgreement: function() {
      this.$v.$touch();
      if(this.$v.selectedLoanPurposeId.$invalid
        || this.$v.selectedLoanTerm.$invalid
        || this.$v.requestedLoanAmount.$invalid
        || this.$v.jointBorrower.$invalid) {
        this.$refs.errorModalRef.show();
      } else {
        this.$refs.disclosureModalRef.show();
      }
    },
    acceptAgreement: function () {
      this.agreementAccepted = true;
      if(this.challengeQuestionsEnabled) {
        // this.$router.push("verification"); // TODO
        this.$router.push("identification");
      } else {
        this.$router.push("identification");
      }
    },
    cancel: function () {
      this.$refs.disclosureModalRef.hide();
    },
    reset() {   
      this.$v.$reset();
      this.selectedLoanType = {};
      this.selectedLoanPurpose = {};
      this.selectedLoanTerm = {};
      this.requestedLoanAmount = null;
      this.jointBorrower = null;
    },
    email: function() {
      this.$v.$reset();
      this.$refs.emailModal.show();
    },
    sendEmail: async function() {
      this.$v.$touch();
      if (this.$v.emailAddress.$invalid) {
        return;
      } else {
        let success = await this.sendEsignAgreement(this.emailAddress);
        if(success) {        
          this.$refs.emailModal.hide();
          this.$refs.emailSentModalRef.show();                
        } else {
          this.$refs.invalidEmailModalRef.show();
        }
      }
    }
  },
  validations: { 
    selectedLoanPurposeId: {
      required: requiredIf(function() {
        return this.eligiblePurposes.length > 0;
      })
    },
    selectedLoanTerm: {
      required: requiredIf(function () {
        return this.eligibleTerms.length > 0;
      }),  
    },      
    requestedLoanAmount: {
      required,
      decimal,
      between (value) {
        if(this.selectedLoanTerm.minAmount && this.selectedLoanTerm.maxAmount) {
          return between(this.selectedLoanTerm.minAmount,this.selectedLoanTerm.maxAmount)(value);
        } else {
          return true;
        }
      },
    },
    jointBorrower: {
      required,
    },
    emailAddress: {          
      email,
      required
    },
  }
};
</script>
